<template>
    <section class="rtb-form-section" data-field="frame_settings">
        <fieldset class="rtb-form-fieldset">
            <legend>Ставки по экранам</legend>
            <p class="field__hint">
                Для каждого креатива на каждом экране задайте ставку и лимиты.
                Пустые поля — без ограничений (используются лимиты кампании).
                Корзина удаляет связку, если по ней ещё не было показов. После первых показов связку можно только снять с ротации — факты сохраняются.
            </p>
            <p v-if="errors.frame_settings" class="field__error">{{ errors.frame_settings }}</p>

            <div v-for="creativeId in matrix.selectedCreativeIdsSorted.value" :key="creativeId" class="creative-frame-block">
                <div
                    class="creative-frame-header"
                    :class="{ 'creative-frame-header--expanded': matrix.expandedCreatives[creativeId] }"
                    @click="matrix.toggleCreativeExpand(creativeId)"
                >
                    <span
                        class="creative-frame-chevron"
                        :class="{ 'creative-frame-chevron--open': matrix.expandedCreatives[creativeId] }"
                        aria-hidden="true"
                    >
                        <img src="/images/arrow_down.svg" alt="" />
                    </span>
                    <div class="creative-frame-header-main">
                        <span class="creative-frame-title">{{ matrix.getCreativeName(creativeId) }}</span>
                    </div>
                    <div
                        v-if="matrix.creativeStatsSummaryById.value[creativeId]"
                        class="creative-frame-summary"
                        @click.stop
                    >
                        <div class="creative-frame-summary-cell">
                            <span class="creative-frame-summary-label">Показы</span>
                            <span
                                class="creative-frame-summary-value"
                                :title="`Сегодня: ${matrix.creativeStatsSummaryById.value[creativeId].impr_daily}`"
                            >{{ matrix.formatNumber(matrix.creativeStatsSummaryById.value[creativeId].impressions) }}</span>
                        </div>
                        <div class="creative-frame-summary-cell">
                            <span class="creative-frame-summary-label">Бюджет</span>
                            <span class="creative-frame-summary-value">{{
                                matrix.formatMoney(matrix.creativeStatsSummaryById.value[creativeId].primary_spent)
                            }}</span>
                        </div>
                        <div class="creative-frame-summary-cell">
                            <span class="creative-frame-summary-label">Среднее</span>
                            <span class="creative-frame-summary-value">{{
                                matrix.creativeStatsSummaryById.value[creativeId].impressions > 0
                                    ? matrix.formatMoney(
                                        matrix.creativeStatsSummaryById.value[creativeId].primary_spent
                                            / matrix.creativeStatsSummaryById.value[creativeId].impressions
                                    )
                                    : '—'
                            }}</span>
                        </div>
                        <div class="creative-frame-summary-cell">
                            <span class="creative-frame-summary-label">Последний показ</span>
                            <span class="creative-frame-summary-value creative-frame-summary-value--muted">{{
                                matrix.formatLastShowAtKzCompact(
                                    matrix.creativeStatsSummaryById.value[creativeId].last_show_at_kz
                                )
                            }}</span>
                        </div>
                    </div>
                </div>

                <div v-if="matrix.expandedCreatives[creativeId]" class="frame-settings-table-wrapper">
                    <table class="frame-settings-table">
                        <thead>
                            <tr>
                                <th>Экран</th>
                                <th v-if="audienceEnabled">Контакты</th>
                                <th>Активен</th>
                                <th>Ставка</th>
                                <th>Бюджет, ₸</th>
                                <th>Лимит показов</th>
                                <template v-if="isEdit">
                                    <th>Показов</th>
                                    <th>Расходы</th>
                                    <th>Последний показ (KZ)</th>
                                </template>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="gid in matrix.getFramesForCreative(creativeId)" :key="gid">
                                <tr :class="{ 'frame-row--paused': !matrix.isFrameInRotation(creativeId, gid) }">
                                    <td class="frame-name-cell">
                                        <span class="frame-label">{{ matrix.getFrameLabel(gid) }}</span>
                                        <span class="frame-gid-text">{{ gid }}</span>
                                    </td>
                                    <td v-if="audienceEnabled" class="frame-ots-cell">{{ matrix.frameOtsLabel(gid) }}</td>
                                    <td class="frame-rotation-cell">
                                        <div class="frame-rotation-stack">
                                            <input
                                                type="checkbox"
                                                class="frame-rotation-checkbox"
                                                :checked="matrix.isFrameInRotation(creativeId, gid)"
                                                :disabled="!canWrite"
                                                @change="matrix.setFrameEnabled(creativeId, gid, $event.target.checked)"
                                            />
                                            <button
                                                type="button"
                                                class="frame-schedule-btn"
                                                :class="{ 'frame-schedule-btn--active': matrix.isScheduleOpen(creativeId, gid) }"
                                                title="Расписание показов"
                                                @click="matrix.toggleSchedule(creativeId, gid)"
                                            >
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                                    <circle cx="12" cy="12" r="10" />
                                                    <polyline points="12 6 12 12 16 14" />
                                                </svg>
                                            </button>
                                        </div>
                                    </td>
                                    <td class="frame-limit-cell frame-bid-cell">
                                        <div class="frame-limit-stack">
                                            <label class="frame-limit-item">
                                                <span class="frame-limit-label">Стратегия</span>
                                                <select
                                                    :value="matrix.getFrameSetting(creativeId, gid, 'bid_strategy') || 'auto'"
                                                    class="frame-input frame-strategy-select"
                                                    :disabled="!canWrite"
                                                    @change="matrix.setFrameStringSetting(creativeId, gid, 'bid_strategy', $event.target.value)"
                                                >
                                                    <option value="manual">Ручная</option>
                                                    <option value="auto">Авто</option>
                                                </select>
                                            </label>
                                            <label class="frame-limit-item">
                                                <span class="frame-limit-label">Bid Price, ₸</span>
                                                <template v-if="(matrix.getFrameSetting(creativeId, gid, 'bid_strategy') || 'auto') === 'manual'">
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        placeholder="—"
                                                        class="frame-input"
                                                        :value="matrix.getFrameSetting(creativeId, gid, 'bid_price')"
                                                        :disabled="!canWrite"
                                                        @input="matrix.setFrameSetting(creativeId, gid, 'bid_price', $event.target.value)"
                                                    />
                                                </template>
                                                <template v-else>
                                                    <span class="auto-bid-badge" :title="matrix.getAutoBidTooltip(creativeId, gid)">
                                                        Авто
                                                        <span
                                                            v-if="matrix.getFrameSetting(creativeId, gid, 'auto_bid_current')"
                                                            class="auto-bid-value"
                                                        >
                                                            {{ matrix.formatMoneyPrecise(matrix.getFrameSetting(creativeId, gid, 'auto_bid_current')) }}
                                                        </span>
                                                        <span v-else class="auto-bid-value auto-bid-value--pending">ожидание</span>
                                                    </span>
                                                </template>
                                            </label>
                                        </div>
                                    </td>
                                    <td class="frame-limit-cell">
                                        <div class="frame-limit-stack">
                                            <label class="frame-limit-item">
                                                <span class="frame-limit-label">Общий бюджет</span>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    placeholder="—"
                                                    class="frame-input"
                                                    :value="matrix.getFrameSetting(creativeId, gid, 'budget_total_limit')"
                                                    :disabled="!canWrite"
                                                    @input="matrix.setFrameSetting(creativeId, gid, 'budget_total_limit', $event.target.value)"
                                                />
                                            </label>
                                            <label class="frame-limit-item">
                                                <span class="frame-limit-label">Дневной бюджет</span>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    placeholder="—"
                                                    class="frame-input"
                                                    :value="matrix.getFrameSetting(creativeId, gid, 'budget_daily_limit')"
                                                    :disabled="!canWrite"
                                                    @input="matrix.setFrameSetting(creativeId, gid, 'budget_daily_limit', $event.target.value)"
                                                />
                                            </label>
                                        </div>
                                    </td>
                                    <td class="frame-limit-cell">
                                        <div class="frame-limit-stack">
                                            <label class="frame-limit-item">
                                                <span class="frame-limit-label">Общий лимит</span>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    placeholder="—"
                                                    class="frame-input"
                                                    :value="matrix.getFrameSetting(creativeId, gid, 'total_impr_limit')"
                                                    :disabled="!canWrite"
                                                    @input="matrix.setFrameSetting(creativeId, gid, 'total_impr_limit', $event.target.value)"
                                                />
                                            </label>
                                            <label class="frame-limit-item">
                                                <span class="frame-limit-label">Дневной лимит</span>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    placeholder="—"
                                                    class="frame-input"
                                                    :value="matrix.getFrameSetting(creativeId, gid, 'daily_impr_limit')"
                                                    :disabled="!canWrite"
                                                    @input="matrix.setFrameSetting(creativeId, gid, 'daily_impr_limit', $event.target.value)"
                                                />
                                            </label>
                                        </div>
                                    </td>
                                    <template v-if="isEdit">
                                        <td class="frame-stats-cell">
                                            <span
                                                class="frame-stats-value"
                                                :title="`Сегодня: ${matrix.getFrameStats(creativeId, gid)?.impr_daily ?? 0}`"
                                            >
                                                {{ matrix.getFrameStats(creativeId, gid)?.impr_total ?? 0 }}
                                            </span>
                                        </td>
                                        <td class="frame-stats-cell frame-limit-cell">
                                            <RtbMoneyLines
                                                :columns="matrix.getFrameStats(creativeId, gid)?.columns || matrix.moneyColumns.value"
                                                :show-avg="true"
                                                :impressions="Number(matrix.getFrameStats(creativeId, gid)?.impr_total ?? 0)"
                                                precise
                                                boxed
                                            />
                                        </td>
                                        <td class="frame-stats-cell frame-stats-cell--wrap">
                                            <span class="frame-stats-value frame-stats-value--muted frame-last-show-at-kz">
                                                {{ matrix.formatLastShowAtKz(matrix.getFrameStats(creativeId, gid)?.last_show_at_kz) }}
                                            </span>
                                        </td>
                                    </template>
                                    <td class="frame-actions-cell">
                                        <button
                                            v-if="canWrite && matrix.canDeleteFrameBinding(creativeId, gid)"
                                            type="button"
                                            class="frame-remove-btn"
                                            title="Удалить связку креатив–экран"
                                            @click="matrix.removeFrameFromCreative(creativeId, gid)"
                                        >
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                                <polyline points="3 6 5 6 21 6" />
                                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                                <path d="M10 11v6" />
                                                <path d="M14 11v6" />
                                                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                            </svg>
                                        </button>
                                        <button
                                            v-if="canWrite && !matrix.isFrameInRotation(creativeId, gid)"
                                            type="button"
                                            class="frame-enable-btn"
                                            @click="matrix.setFrameEnabled(creativeId, gid, true)"
                                        >
                                            Включить
                                        </button>
                                    </td>
                                </tr>
                                <tr v-if="matrix.isScheduleOpen(creativeId, gid)" class="schedule-row">
                                    <td :colspan="matrix.frameSettingsColCount.value" class="schedule-cell-td">
                                        <RtbScheduleGrid
                                            :model-value="matrix.getSchedule(creativeId, gid)"
                                            :start-date="form.start_date"
                                            :end-date="form.end_date"
                                            :is-admin="isAdmin"
                                            :editable="canWrite"
                                            :campaign-id="campaign?.id || null"
                                            :campaign-schedule="campaignSchedule"
                                            :creative-id="creativeId"
                                            :screen-id="gid"
                                            :initial-frequencies="matrix.frameFrequenciesMap[`${creativeId}:${gid}`] || null"
                                            :baseline-frequencies="matrix.campaignFrequencies.value"
                                            :creative-length-seconds="matrix.getCreativeLengthSeconds(creativeId)"
                                            @update:modelValue="matrix.setSchedule(creativeId, gid, $event)"
                                            @frequencies-updated="matrix.onFrequenciesUpdated"
                                        />
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>
            </div>
        </fieldset>
    </section>
</template>

<script setup>
import RtbScheduleGrid from '../RtbScheduleGrid.vue';
import RtbMoneyLines from '../../RtbMoneyLines.vue';

defineProps({
    matrix: { type: Object, required: true },
    errors: { type: Object, default: () => ({}) },
    audienceEnabled: { type: Boolean, default: false },
    isEdit: { type: Boolean, default: false },
    canWrite: { type: Boolean, default: true },
    isAdmin: { type: Boolean, default: false },
    campaign: { type: Object, default: null },
    form: { type: Object, required: true },
    campaignSchedule: { type: Array, default: () => [] },
});
</script>
